import { Button, Modal, Portal, Typography } from '@mui/material'
import { Box } from '@mui/system'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useEffect } from 'react';
import { convertToY, isYArray, isYMap } from '../hooks/documentHooks';
import { useSearchTerm } from './SearchContext';

const ModalContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(216px, 1fr) minmax(216px, 1fr);
  max-height: 55vh;
  overflow-y: auto;
`

const TemplateContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  cursor: pointer;

  &:hover {
    background: #bfffbf;
  }

  template-title {
    font-weight: bolder;
  }

  image-container {
    display: flex;
    width: 200px;
    height: 120px;
    img {
      display: flex;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
`

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: '#efefef',
  borderRadius: '8px',
  boxShadow: 24,
  p: 4,
}

const buttonsContainerStyle = { 
  p: 1, 
  display: 'flex', 
  justifyContent: 'flex-end' 
}

const TemplatePreview = ({ template, onSelected }) => {
  const handleClick = useCallback(() => {
    if (onSelected) onSelected(template)
  }, [onSelected, template])

  return (
    <TemplateContainer onClick={handleClick}>
      <template-title>
        {template.title}
        {template.description && 
          <Tooltip title={template.description}>
            <IconButton>
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        }
      </template-title>
      <image-container>
        {template.image && <img src={template.image} alt={template.title} />}
      </image-container>
    </TemplateContainer>
  )
}

const InsertTemplateModal = () => {
  const rawData = useSelector(state => state.InsertTemplateModal || {})
  const [data, setData] = useState()
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const { setSearchTerm } = useSearchTerm()
  
  const handleSelection = useCallback((template, fields) => {
    const object = data.object
    const templateCopy = convertToY(JSON.parse(JSON.stringify(template.value)))
    if (isYArray(object)) {
      object.push([templateCopy])
      setSearchTerm({ path: `${data.path}[${object.length - 1}]` })
    } else if (isYMap(object)) {
      const name = fields?.name || 'Object' + Math.round(Math.random() * 10000)
      object.set(name, templateCopy)
      setSearchTerm({ path: `${data.path}.${name}` })
    } else {
      throw new Error('Not an Y object to work')
    }
    dispatch({ type: 'insert-template-modal' })
  }, [data, dispatch, setSearchTerm])

  const handleClose = useCallback(() => {
    dispatch({ type: 'insert-template-modal' })
  }, [dispatch])

  useEffect(() => {
    if (rawData?.templates?.length > 0) {
      setData(rawData)
      setOpen(true)
    } else {
      setOpen(false)
    }
  }, [rawData])

  return (
    <Portal>
      {data?.templates &&
        <Modal open={open}>
          <Box sx={modalStyle}>
            <Typography id="modal-modal-title" variant="h5" component="h2">
              Select a template
            </Typography>
            <ModalContainer>
              {data?.templates.map((template, index) => <TemplatePreview key={index} template={template} onSelected={handleSelection} />)}
            </ModalContainer>
            <Box sx={buttonsContainerStyle}>
              <Button variant="outlined" onClick={handleClose}>Cancel</Button>
            </Box>
          </Box>
        </Modal>
      }
    </Portal>
  )
}

export default InsertTemplateModal